import type { IDeploymentEngine } from './IDeploymentEngine.interface'
import type {
  ComparisonResult,
  Deployment,
  DeploymentEvent,
  DeploymentFilter,
  DeploymentInput,
  DeploymentRule,
  Environment,
  Stage,
  VerificationResult,
} from '../../types/deployment.types'

// ── Static seed data ──────────────────────────────────────────────────────────

const MOCK_ENVIRONMENTS: Environment[] = [
  {
    id:                'arm-source',
    name:              'arm-source',
    type:              'source',
    instanceUrl:       'https://stratuscarta-c.my.salesforce.com',
    username:          'admin@stratuscarta-c.com',
    status:            'connected',
    lastDeployedAt:    new Date(Date.now() - 86_400_000).toISOString(),
    branch:            'main',
    alias:             'prod-full',
    stage:             'PROD',
    activeDeploymentId: null,
  },
  {
    id:                'arm-target',
    name:              'arm-target',
    type:              'target',
    instanceUrl:       'https://st1750093282865-dev-ed.develop.my.salesforce.com',
    username:          'admin@arm-target.com',
    status:            'connected',
    lastDeployedAt:    null,
    branch:            'phase/2-arm-engine',
    alias:             'dev-01',
    stage:             'DEV',
    activeDeploymentId: 'dep-hist-001',  // non-null → branch shows pulsing deployment dot
  },
]

// ── Default layers the mock engine deploys ────────────────────────────────────
// Engine decides what to deploy — UI never passes layers. These are the layers
// this mock always runs, in order.

export const MOCK_DEFAULT_LAYERS = ['01', '02', '03'] as const

interface LayerMeta {
  layer: string
  name: string
  records: number
  notes: string | null
}

const ALL_LAYER_META: LayerMeta[] = [
  { layer: '00', name: 'GUID Setup',              records:   0, notes: 'Apex — both orgs' },
  { layer: '01', name: 'Attribute Foundation',    records:  71, notes: null },
  { layer: '02', name: 'Product Classification',  records:  37, notes: null },
  { layer: '03', name: 'Products',                records:  46, notes: 'Type retry applied' },
  { layer: '04', name: 'Tax Config',              records:   0, notes: 'Skipped — not in org' },
  { layer: '05', name: 'Attribute Wiring',        records:  74, notes: '28 wired · 46 activated' },
  { layer: '06', name: 'Selling Models',          records:   0, notes: 'Skipped — not in org' },
  { layer: '07', name: 'Bundle Structure',        records:  68, notes: '9/9 bundles verified' },
  { layer: '08', name: 'Catalog',                 records:  38, notes: null },
  { layer: '09', name: 'Pricing',                 records:  48, notes: 'Standard PB stamped' },
]

// Five representative rules from arm_deployment_rules.md
const MOCK_RULES: DeploymentRule[] = [
  {
    id: 'L1-1',
    layer: '01',
    title: 'DeveloperName is INSERT-only — excluded from all payloads',
    description:
      'DeveloperName can only be set on record creation and cannot be changed. ' +
      'Re-runs will not update this field; the value set during the initial INSERT is preserved.',
    severity: 'warn',
  },
  {
    id: 'L3-1',
    layer: '03',
    title: 'Product2.CanRamp is not writeable — this field will NOT be migrated',
    description:
      'CanRamp is a Revenue Cloud platform-managed field that cannot be set via the Data API. ' +
      'It may be set automatically by the platform or require a special permission.',
    severity: 'warn',
  },
  {
    id: 'L6-4',
    layer: '06',
    title: 'All three Selling Model objects may be absent — layer will skip entirely',
    description:
      'ProductSellingModel, ProductSellingModelOption, and ProductRampSegment are only ' +
      'available in orgs with Revenue Cloud Selling Models enabled. If all three return ' +
      '0 records the layer exits with status "skipped".',
    severity: 'info',
  },
  {
    id: 'L7-5',
    layer: '07',
    title: 'Static bundle constraints — three rules enforced simultaneously',
    description:
      'Static bundle parents require: IsDefaultComponent=True, IsQuantityEditable=False, ' +
      'and no MaxQuantity/MinQuantity. All three constraints must be satisfied together. ' +
      'The retry is applied automatically when any of the three constraint errors fires.',
    severity: 'warn',
  },
  {
    id: 'L9-2',
    layer: '09',
    title: 'Standard Pricebook2 cannot be inserted — GlobalKey__c stamped onto existing record',
    description:
      'Every org has exactly one system-generated standard Pricebook2 that cannot be inserted. ' +
      'The migration stamps the source GlobalKey__c onto the target\'s existing standard PB ' +
      'using patch_by_sfid() rather than upsert.',
    severity: 'info',
  },
]

// ── Pre-seeded history ────────────────────────────────────────────────────────

const src = MOCK_ENVIRONMENTS[0]!
const tgt = MOCK_ENVIRONMENTS[1]!

const MOCK_HISTORY: Deployment[] = [
  {
    id:          'dep-hist-001',
    status:      'complete',
    startedAt:   new Date(Date.now() -  2 * 86_400_000).toISOString(),
    completedAt: new Date(Date.now() -  2 * 86_400_000 + 120_000).toISOString(),
    sourceEnv:   { ...src },
    targetEnv:   { ...tgt },
    recordCount: 154,
    errorCount:  0,
    initiatedBy: 'Anuj Vaishampayan',
  },
  {
    id:          'dep-hist-002',
    status:      'complete',
    startedAt:   new Date(Date.now() -  5 * 86_400_000).toISOString(),
    completedAt: new Date(Date.now() -  5 * 86_400_000 +  90_000).toISOString(),
    sourceEnv:   { ...src },
    targetEnv:   { ...tgt },
    recordCount:  98,
    errorCount:   0,
    initiatedBy: 'Anuj Vaishampayan',
  },
]

// ── Helper ────────────────────────────────────────────────────────────────────

function layerMeta(layer: string): LayerMeta {
  return ALL_LAYER_META.find((l) => l.layer === layer) ?? {
    layer, name: `Layer ${layer}`, records: 0, notes: null,
  }
}

function now(): string {
  return new Date().toISOString()
}

// ── MockDeploymentEngine ──────────────────────────────────────────────────────

export class MockDeploymentEngine implements IDeploymentEngine {
  private readonly deployments = new Map<string, Deployment>()
  /** Per-deployment stage state — keyed by deploymentId, then by layer */
  private readonly _stageMap   = new Map<string, Map<string, Stage>>()

  constructor() {
    // Pre-seed history so listDeployments() returns data without starting a run
    for (const dep of MOCK_HISTORY) {
      this.deployments.set(dep.id, { ...dep })
    }
  }

  // ── Environments ────────────────────────────────────────────────────────────

  listEnvironments(): Promise<Environment[]> {
    return Promise.resolve([...MOCK_ENVIRONMENTS])
  }

  getEnvironment(id: string): Promise<Environment> {
    const env = MOCK_ENVIRONMENTS.find((e) => e.id === id)
    if (!env) return Promise.reject(new Error(`Environment '${id}' not found`))
    return Promise.resolve({ ...env })
  }

  // ── Deployments ─────────────────────────────────────────────────────────────

  startDeployment(input: DeploymentInput): Promise<Deployment> {
    const sourceEnv = MOCK_ENVIRONMENTS.find((e) => e.id === input.sourceEnvId)
    const targetEnv = MOCK_ENVIRONMENTS.find((e) => e.id === input.targetEnvId)
    if (!sourceEnv) return Promise.reject(new Error(`Unknown sourceEnvId '${input.sourceEnvId}'`))
    if (!targetEnv) return Promise.reject(new Error(`Unknown targetEnvId '${input.targetEnvId}'`))

    const deployment: Deployment = {
      id:          globalThis.crypto.randomUUID(),
      status:      'running',
      startedAt:   now(),
      completedAt: null,
      sourceEnv:   { ...sourceEnv },
      targetEnv:   { ...targetEnv },
      recordCount: 0,
      errorCount:  0,
      initiatedBy: input.initiatedBy ?? 'mock-user',
    }

    this.deployments.set(deployment.id, deployment)

    // Pre-populate pending stages for the default layers
    const stageMap = new Map<string, Stage>()
    for (const layerId of MOCK_DEFAULT_LAYERS) {
      const meta = layerMeta(layerId)
      stageMap.set(layerId, {
        layer:       layerId,
        name:        meta.name,
        status:      'pending',
        recordCount: null,
        errors:      0,
        startedAt:   null,
        completedAt: null,
        durationMs:  null,
        notes:       meta.notes,
      })
    }
    this._stageMap.set(deployment.id, stageMap)

    return Promise.resolve({ ...deployment })
  }

  getDeployment(id: string): Promise<Deployment> {
    const dep = this.deployments.get(id)
    if (!dep) return Promise.reject(new Error(`Deployment '${id}' not found`))
    return Promise.resolve({ ...dep })
  }

  listDeployments(_filter?: DeploymentFilter): Promise<Deployment[]> {
    // Newest first
    return Promise.resolve(
      [...this.deployments.values()]
        .sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime())
        .map((d) => ({ ...d })),
    )
  }

  cancelDeployment(id: string): Promise<void> {
    const dep = this.deployments.get(id)
    if (!dep) return Promise.reject(new Error(`Deployment '${id}' not found`))
    this.deployments.set(id, { ...dep, status: 'cancelled', completedAt: now() })
    return Promise.resolve()
  }

  // ── Detail / verification ─────────────────────────────────────────────────

  getDeploymentStages(deploymentId: string): Promise<Stage[]> {
    const dep = this.deployments.get(deploymentId)
    if (!dep) return Promise.reject(new Error(`Deployment '${deploymentId}' not found`))

    const stageMap = this._stageMap.get(deploymentId)
    if (!stageMap) return Promise.resolve([])

    return Promise.resolve([...stageMap.values()])
  }

  getVerificationResults(deploymentId: string): Promise<VerificationResult[]> {
    const dep = this.deployments.get(deploymentId)
    if (!dep) return Promise.reject(new Error(`Deployment '${deploymentId}' not found`))

    const results: VerificationResult[] = [
      {
        check:    'Product2 count ≥ source',
        layer:    '03',
        result:   'pass',
        count:    46,
        expected: 46,
        notes:    null,
      },
      {
        check:    'AttributeDefinition count ≥ source',
        layer:    '01',
        result:   'pass',
        count:    71,
        expected: 71,
        notes:    null,
      },
      {
        check:    'ProductClassification count ≥ source',
        layer:    '02',
        result:   'pass',
        count:    37,
        expected: 37,
        notes:    null,
      },
      {
        check:    'BasedOnId set on migrated records',
        layer:    '05',
        result:   'skip',
        count:    null,
        expected: null,
        notes:    'No BasedOnId relationships in source',
      },
      {
        check:    'ProductSellingModelOption migrated',
        layer:    '06',
        result:   'skip',
        count:    0,
        expected: null,
        notes:    'Layer 06 skipped — object not in org',
      },
    ]

    return Promise.resolve(results)
  }

  getDeploymentRules(): Promise<DeploymentRule[]> {
    return Promise.resolve([...MOCK_RULES])
  }

  // ── Real-time streaming simulation ────────────────────────────────────────

  subscribeToDeployment(
    deploymentId: string,
    onEvent: (event: DeploymentEvent) => void,
  ): () => void {
    const dep = this.deployments.get(deploymentId)
    if (!dep) throw new Error(`Deployment '${deploymentId}' not found`)

    // Don't stream for already-complete historical deployments
    if (dep.status !== 'running') {
      return () => { /* no-op */ }
    }

    const timeouts: number[] = []
    let cancelled = false

    const emit = (event: DeploymentEvent): void => {
      if (!cancelled) onEvent(event)
    }

    const after = (fn: () => void, delayMs: number): void => {
      timeouts.push(setTimeout(fn, delayMs) as unknown as number)
    }

    let cursor = 0

    for (const layerId of MOCK_DEFAULT_LAYERS) {
      const meta = layerMeta(layerId)
      const base = cursor

      // Stage start
      after(() => {
        const sm = this._stageMap.get(deploymentId)
        const s  = sm?.get(layerId)
        if (sm && s) sm.set(layerId, { ...s, status: 'running', startedAt: now() })
        emit({ type: 'stage', layer: layerId, message: `Starting layer ${layerId} — ${meta.name}`, timestamp: now() })
      }, base)

      after(() => emit({
        type:      'log',
        layer:     layerId,
        message:   `Layer ${layerId}: querying arm-source...`,
        timestamp: now(),
      }), base + 800)

      after(() => emit({
        type:      'log',
        layer:     layerId,
        message:   `Layer ${layerId}: ${meta.records} records fetched`,
        timestamp: now(),
      }), base + 850)

      after(() => emit({
        type:      'log',
        layer:     layerId,
        message:   `Layer ${layerId}: upserted ${meta.records} → arm-target`,
        timestamp: now(),
      }), base + 900)

      // Stage complete
      after(() => {
        const sm = this._stageMap.get(deploymentId)
        const s  = sm?.get(layerId)
        if (sm && s) sm.set(layerId, {
          ...s,
          status:      'complete',
          completedAt: now(),
          durationMs:  1400,
          recordCount: meta.records,
        })
        emit({ type: 'stage', layer: layerId, message: `Layer ${layerId} complete — ${meta.records} records`, timestamp: now() })
      }, base + 1400)

      cursor += 1500
    }

    // Final complete event
    after(() => {
      const totalRecords = MOCK_DEFAULT_LAYERS.reduce((acc, l) => acc + layerMeta(l).records, 0)
      emit({ type: 'complete', message: `Deployment complete — ${totalRecords} records · 0 errors`, timestamp: now() })
      const stored = this.deployments.get(deploymentId)
      if (stored) {
        this.deployments.set(deploymentId, {
          ...stored,
          status:      'complete',
          completedAt: now(),
          recordCount: totalRecords,
        })
      }
    }, cursor + 500)

    return () => {
      cancelled = true
      timeouts.forEach((id) => clearTimeout(id))
    }
  }

  // ── Comparison ─────────────────────────────────────────────────────────────

  compareSources(
    left:  { type: 'org' | 'branch'; ref: string },
    right: { type: 'org' | 'branch'; ref: string },
  ): Promise<ComparisonResult> {
    const mode: ComparisonResult['mode'] =
      left.type  === 'org'    && right.type === 'org'    ? 'org-org'
      : left.type === 'branch' && right.type === 'branch' ? 'branch-branch'
      : 'org-branch'

    const result: ComparisonResult = {
      mode,
      leftLabel:  left.ref,
      rightLabel: right.ref,
      groups: [
        { category: 'Products',         leftCount: 46, rightCount: 60, added: 14, removed:  0, modified: 0, inSync: 32 },
        { category: 'Attributes',       leftCount: 71, rightCount: 83, added: 12, removed:  0, modified: 0, inSync: 59 },
        { category: 'Classifications',  leftCount: 37, rightCount: 46, added:  9, removed:  0, modified: 0, inSync: 28 },
        { category: 'Catalog',          leftCount: 38, rightCount: 38, added:  0, removed:  0, modified: 0, inSync: 38 },
        { category: 'Pricing',          leftCount: 48, rightCount: 45, added:  0, removed:  3, modified: 0, inSync: 42 },
        { category: 'Bundles',          leftCount: 68, rightCount: 68, added:  0, removed:  0, modified: 0, inSync: 68 },
      ],
    }
    return Promise.resolve(result)
  }
}
